/**
 * Constructo — client entry point.
 * Loads palette, character, vehicles, props; sets up 3rd/1st-person controls;
 * enforces render budgets; runs the animation loop.
 */
import * as THREE from 'three/webgpu';
import { buildScene, trackAdd } from './systems/scene';
import { loadPalette, getPalette } from './systems/palette';
import {
  createControls,
  updateMoveDir,
  updateCamera,
  type PlayerControls,
} from './systems/controls';
import {
  loadAllGLBs,
  placeAsset,
  countDrawCalls,
  countTextures,
  type LoadedAsset,
} from './assets/loadAssets';
import { loadCharacter, playWalk, playIdle, type CharacterModel } from './assets/load';
import { TerrainManager } from './world/Terrain';
import { buildSky } from './systems/sky';
import { buildRoads } from './systems/roads';
import { buildWater } from './systems/water';

// ─── Renderer ───────────────────────────────────────────────────────
const renderer = new THREE.WebGPURenderer({ antialias: true, forceWebGL: false });
renderer.setSize(innerWidth, innerHeight);
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.15;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
document.body.appendChild(renderer.domElement);

const { scene, budget } = buildScene();
const camera = new THREE.PerspectiveCamera(
  60,
  innerWidth / innerHeight,
  0.1,
  1200, // must exceed sky dome radius (900)
);
const terrain = new TerrainManager(scene);
const SPAWN_HEIGHT = 1.2; // must match padH in Terrain.ts
const groundY = (x: number, z: number): number => terrain.getHeightAt(x, z);

// Environment: sky dome + sun + clouds, crossroads, water in the lowlands.
buildSky(scene, new THREE.Vector3(40, 60, 25));
buildRoads(scene, SPAWN_HEIGHT);
buildWater(scene);

// ─── Resize ─────────────────────────────────────────────────────────
addEventListener('resize', () => {
  camera.aspect = innerWidth / innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(innerWidth, innerHeight);
});

// ─── HUD overlay ────────────────────────────────────────────────────
const hudEl = document.createElement('div');
hudEl.id = 'hud';
hudEl.style.cssText =
  'position:fixed;top:8px;left:8px;color:#fff;font:12px monospace;' +
  'background:rgba(0,0,0,0.6);padding:6px 10px;border-radius:4px;pointer-events:none;z-index:999';
document.body.appendChild(hudEl);

function updateHUD(controls: PlayerControls, clock: THREE.Clock): void {
  // NOTE: clock.getDelta() is consumed once per frame in loop(); calling it
  // again here would return ~0 and report a bogus ~1M fps. The loop stores
  // the real delta on updateHUD.lastDelta each frame — fall back to 60fps.
  const dt = updateHUD.lastDelta ?? 1 / 60;
  const fps = dt > 0 ? Math.round(1 / dt) : 0;
  hudEl.textContent =
    `mode: ${controls.mode} | ` +
    `draw calls: ${budget.drawCalls}/${budget.maxDrawCalls} | ` +
    `textures: ${budget.textures}/${budget.maxTextures} | ` +
    `fps: ${fps} | ` +
    `chunks: ${terrain.chunkCount} (${terrain.triangleCount} tris)`;
}
namespace updateHUD {
  export let lastDelta = 1 / 60;
}

// ─── Load & place everything ────────────────────────────────────────
let character: CharacterModel | null = null;
const characterTarget = new THREE.Object3D(); // pivot for camera tracking
characterTarget.position.set(0, SPAWN_HEIGHT, 0);
scene.add(characterTarget);

async function init(): Promise<void> {
  // 1. Palette
  await loadPalette();

  // 2. Character with AnimationMixer
  character = await loadCharacter('/assets/character.glb');
  character.scene.position.set(0, SPAWN_HEIGHT, 0);
  character.scene.castShadow = true;
  trackAdd(scene, character.scene, budget);

  // 3. Vehicles (4)
  const vehicleNames = ['excavator', 'crane', 'dozer', 'truck'];
  const vehicleAssets = await loadAllGLBs(
    vehicleNames.map((n) => `/assets/${n}.glb`),
  );
  const vehicleXZ: Array<[number, number]> = [[-8, 6], [8, 6], [-8, -6], [8, -6]];
  const vehiclePositions = vehicleXZ.map(([x, z]) => new THREE.Vector3(x, groundY(x, z), z));
  let vehicleDraws = 0;
  vehicleAssets.forEach((asset, i) => {
    asset.scene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) child.castShadow = true;
    });
    vehicleDraws += placeAsset(scene, asset, vehiclePositions[i], budget);
  });
  console.log(
    `[init] vehicles loaded: ${vehicleNames.join(', ')} (${vehicleDraws} draws)`,
  );

  // 4. Construction yard — fixed layout on the flat pad (Task G3).
  // [glb, x, z, rotY]. All y seated via groundY().
  const yard: Array<[string, number, number, number]> = [
    ['site_office', -12, -10, 0.4],
    ['fence', -6, -14, 0], ['fence', -2, -14, 0], ['fence', 2, -14, 0],
    ['pallet', 6, -8, 0.3], ['crate', 7.5, -8, 0],
    ['rocks', 10, 4, 0], ['barrel', 11.5, 4.5, 0],
    ['tree', -14, 8, 0], ['tree', 14, 10, 0], ['tree', 0, 16, 0],
    ['cone_traffic', -4, 2, 0], ['cone_traffic', 4, 2, 0],
    ['portaloo', -14, -4, 1.57], ['concrete_barrier', 0, -12, 0],
    ['barrier', -8, -12, 0], ['barrier', 8, -12, 0],
  ];
  const yardNames = [...new Set(yard.map(([n]) => n))];
  const yardGLBs = await loadAllGLBs(yardNames.map((n) => `/assets/${n}.glb`));
  const yardByName = new Map(yardGLBs.map((a, i) => [yardNames[i], a]));
  let propDraws = 0;
  for (const [name, x, z, rotY] of yard) {
    const src = yardByName.get(name);
    if (!src) {
      console.warn(`[init] yard asset missing: ${name}`);
      continue;
    }
    const inst = src.scene.clone(true);
    inst.position.set(x, groundY(x, z), z);
    inst.rotation.y = rotY;
    inst.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) child.castShadow = true;
    });
    const draws = countDrawCalls(inst);
    budget.drawCalls += draws;
    scene.add(inst);
    propDraws += draws;
  }
  console.log(
    `[init] yard placed: ${yard.length} instances of ${yardNames.join(', ')} (${propDraws} draws)`,
  );

  console.log(
    `[budget] final — draw calls: ${budget.drawCalls}/${budget.maxDrawCalls}, ` +
      `textures: ${budget.textures}/${budget.maxTextures}`,
  );
}

// ─── Controls ───────────────────────────────────────────────────────
const controls = createControls(renderer.domElement);

// ─── Animation loop ─────────────────────────────────────────────────
const clock = new THREE.Clock();
let lastMoveState = false;

function loop(): void {
  requestAnimationFrame(loop);
  const delta = clock.getDelta();
  updateHUD.lastDelta = delta;

  updateMoveDir(controls);
  const moving = controls.moveDir.lengthSq() > 0.001;

  if (character) {
    // Update character position to follow camera target
    character.scene.position.copy(characterTarget.position);
    character.scene.rotation.y = controls.yaw;

    // Animation transitions
    if (moving && !lastMoveState) playWalk(character);
    else if (!moving && lastMoveState) playIdle(character);

    character.mixer.update(delta);
  }

  lastMoveState = moving;
  updateCamera(camera, controls, characterTarget, delta);

  // Update terrain LOD based on camera position
  terrain.update(camera.position.x, camera.position.z);

  renderer.renderAsync(scene, camera);
  updateHUD(controls, clock);
}

// ─── Start ──────────────────────────────────────────────────────────
init().then(() => {
  console.log('[init] all assets loaded, starting loop');
  loop();
});